import { ApiError, request } from "@/lib/api";

export function libraryError(error: Error): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return "You don't have permission to make this change. Contact your workspace administrator.";
    if (error.status === 429) return "Too many signup attempts. Please try again later or subscribe manually.";
    if (error.status === 422) return "Check the competitor name, full website URL and two-letter country code.";
    if (error.status >= 500) return "The email library is temporarily unavailable. Please try again.";
    try {
      const detail = JSON.parse(error.body).detail;
      if (typeof detail === "string") return detail;
    } catch { /* A non-JSON response still gets a readable fallback. */ }
  }
  return "Couldn't complete the request. Check your connection and try again.";
}

export interface CompetitorBrand {
  competitor_brand_id: number;
  name: string;
  website_url: string;
  country: string;
  status: string;
  receiving_address?: string | null;
  signup_error?: string | null;
  can_retry_signup: boolean;
  total_email_count: number;
  first_email_at?: string | null;
  last_email_at?: string | null;
}
export interface SearchHit {
  competitor_brand_id: number;
  name: string;
  website_url: string;
  country: string;
  already_following: boolean;
}
export interface BrandSuggestion {
  competitor_brand_id: number;
  name: string;
  website_url: string;
  vertical: string | null;
  country: string;
  reason: string;
  email_count: number;
  last_email_at: string | null;
}
export interface SuggestionsResponse {
  suggestions: BrandSuggestion[];
  tenant_verticals: string[];
}
export interface EmailCard {
  email_id: number;
  competitor_brand_id: number;
  competitor_name: string;
  country: string;
  subject: string | null;
  preheader: string | null;
  body_text?: string | null;
  sender_name: string | null;
  sent_at: string | null;
  received_at: string;
  screenshot_thumb_url?: string | null;
  preview_status?: "ready" | "pending" | "failed" | "partial";
  preview_error?: string | null;
  tags?: string[];
  tag_source?: string | null;
  flow_type?: string;
  offer_type?: string | null;
  angle?: string | null;
  is_bookmarked: boolean;
}
export interface EmailDetail extends EmailCard {
  brand: { name: string; country: string };
  sender_email: string | null;
  body_html_url: string | null;
  processing_status?: string;
  processing_error?: string | null;
}
export interface EmailList {
  emails: EmailCard[];
  total: number;
  unfiltered_total: number;
  page: number;
  page_size: number;
}
export interface EmailFilters {
  competitor_brand_id?: string;
  country?: string;
  q?: string;
  email_type?: string;
  date_min?: string;
  date_max?: string;
  bookmarked_only?: boolean;
  sort: string;
  page: number;
}
export interface CalendarResponse {
  competitor_brand_id: number;
  days: number;
  per_day: { date: string; count: number }[];
  heatmap: number[][];
  offers: { date: string; offer_type: string; offer_value: string | null; email_id: number }[];
  type_mix: { email_type: string; count: number }[];
  cadence_per_week: number;
}
export interface InsightsResponse {
  days: number;
  competitors: { competitor_brand_id: number; name: string; sends: number; cadence_per_week: number; top_type: string | null; top_offer: string | null }[];
  this_week: { email_id: number; competitor_name: string; subject: string | null; sent_at: string | null; email_type: string | null; offer_type: string | null; offer_value: string | null }[];
  angles: { angle: string; count: number }[];
}
const base = (slug: string) => `/api/brands/${encodeURIComponent(slug)}/competitor-emails`;
export const competitorEmailsApi = {
  brands: (slug: string) => request<{ brands: CompetitorBrand[] }>("GET", `${base(slug)}/brands`),
  search: (slug: string, query: string) => request<{ hits: SearchHit[] }>("POST", `${base(slug)}/brands/search`, { query }),
  suggestions: (slug: string) => request<SuggestionsResponse>("GET", `${base(slug)}/suggestions`),
  follow: (slug: string, id: number) => request("POST", `${base(slug)}/brands/${id}/follow`),
  unfollow: (slug: string, id: number) => request("DELETE", `${base(slug)}/brands/${id}/follow`),
  add: (slug: string, body: { name: string; website_url: string; country: string }) => request<CompetitorBrand & { created: boolean }>("POST", `${base(slug)}/brands`, body),
  subscribe: (slug: string, id: number) => request("POST", `${base(slug)}/brands/${id}/subscribe`),
  setup: (slug: string, id: number) => request<{ handoff?: { url: string; expires_at: string } | null; emails: { email_id: number; subject: string | null; body_text: string | null; received_at: string; processing_status: string; processing_error?: string | null; links?: string[] }[] }>("GET", `${base(slug)}/brands/${id}/setup`),
  emails: (slug: string, filters: EmailFilters) => {
    const qs = new URLSearchParams({ page_size: "24" });
    Object.entries(filters).forEach(([key, value]) => { if (value !== "" && value != null) qs.set(key, String(value)); });
    return request<EmailList>("GET", `${base(slug)}/emails?${qs}`);
  },
  detail: (slug: string, id: number) => request<{ email: EmailDetail }>("GET", `${base(slug)}/emails/${id}`),
  bookmark: (slug: string, id: number) => request<{ bookmarked: boolean }>("POST", `${base(slug)}/emails/${id}/bookmark`),
  calendar: (slug: string, id: number, days = 90) => request<CalendarResponse>("GET", `${base(slug)}/brands/${id}/calendar?days=${days}`),
  insights: (slug: string, days = 7) => request<InsightsResponse>("GET", `${base(slug)}/insights?days=${days}`),
};

// Forward-in intake (Plan D).
export interface Intake { address: string; status: string; allowed_forwarders: string[]; default_country: string; rotated_at: string | null }
export interface IntakeEvent { event_id: number; received_at: string; kind: string; status: "added" | "needs_review" | "rejected" | "duplicate"; reason?: string | null; subject?: string | null; outer_sender?: string | null; email_id?: number | null; competitor_brand_id?: number | null; competitor_name?: string | null; candidate_domain?: string | null }
export interface IntakeResponse { intake: Intake | null; events: IntakeEvent[] }
const intakeBase = (slug: string) => `${base(slug)}/intake`;
export const intakeApi = {
  get: (slug: string) => request<IntakeResponse>("GET", intakeBase(slug)),
  create: (slug: string) => request<IntakeResponse>("POST", intakeBase(slug)),
  rotate: (slug: string) => request<IntakeResponse>("POST", `${intakeBase(slug)}/rotate`),
  setForwarders: (slug: string, allowed_forwarders: string[]) => request<IntakeResponse>("PUT", `${intakeBase(slug)}/forwarders`, { allowed_forwarders }),
  assign: (slug: string, eventId: number, body: { competitor_brand_id?: number; create?: { name: string; website_url: string; country: string } }) => request<{ events: IntakeEvent[] }>("POST", `${intakeBase(slug)}/events/${eventId}/assign`, body),
  upload: async (slug: string, file: File): Promise<{ events: IntakeEvent[] }> => {
    const form = new FormData(); form.append("file", file);
    const response = await fetch(`${intakeBase(slug)}/upload`, { method: "POST", credentials: "same-origin", body: form });
    if (!response.ok) throw new ApiError(response.status, await response.text(), `${intakeBase(slug)}/upload`);
    return (await response.json()) as { events: IntakeEvent[] };
  },
};

// Remix (Plan G / v2): a competitor email rebuilt as the tenant's editable Klaviyo template.
export type RemixStep = "extracting" | "structure" | "brand" | "campaign" | "fill" | "build" | "ready" | "failed";
export interface RemixSlot { id: string; kind: "image" | "products" | "text" | "button"; role: string; width: number | null; height: number | null; columns: number | null; competitor_text: string }
export interface ImageCandidate { id: string; url: string; name: string | null; width: number | null; height: number | null; source: string; labels: Record<string, unknown> }
export interface ProductCandidate { id: string; name: string; price: string; image_url: string; url: string; product_type?: string; tags?: string[] }
export interface CouponPool { id: string; name: string; description: string; codes: number }
export interface RemixCandidates { logo: ImageCandidate[]; images: ImageCandidate[]; products: ProductCandidate[]; storefront_url: string; brand_domains: string[]; coupon_pools?: CouponPool[] }
export interface RemixGate { passed: boolean; reason: string }
export type RemixChoice = { url: string; alt?: string } | { products: ProductCandidate[] };
export interface RemixPosition { slot_id: string; section: number; kind: "image" | "products"; role: string; width: number | null; height: number | null; columns: number | null; chosen: RemixChoice | null; reason: string }
export interface RemixCampaign { campaign_type: string | null; subject: string | null; preheader: string | null; direction: string | null }
export interface RemixLayout { order: number[]; removed: number[] }
export interface RemixSection { index: number; role: string; removed: boolean; positions: string[] }
export interface RemixState {
  remix_id: string;
  status: string;
  step: RemixStep;
  error: string | null;
  source: { email_id: number; competitor_name: string | null; subject: string | null; screenshot_url: string | null };
  slots: RemixSlot[];
  candidates: RemixCandidates;
  choices: Record<string, RemixChoice>;
  copy: Record<string, string>;
  name: string | null;
  coupon: string | null;
  campaign: RemixCampaign | null;
  positions: RemixPosition[];
  layout: RemixLayout;
  sections: RemixSection[];
  gates: Record<string, RemixGate>;
  klaviyo_template_id: string | null;
  editor_url: string | null;
  preview_url: string;
}
const remixBase = (slug: string) => `${base(slug)}/remixes`;
export const remixApi = {
  start: (slug: string, emailId: number, region?: string) =>
    request<{ remix_id: string }>("POST", `${base(slug)}/emails/${emailId}/remix${region ? `?region=${encodeURIComponent(region)}` : ""}`),
  get: (slug: string, id: string) => request<RemixState>("GET", `${remixBase(slug)}/${id}`),
  update: (slug: string, id: string, body: { choices?: Record<string, RemixChoice>; name?: string; subject?: string; preheader?: string; copy?: Record<string, string>; layout?: RemixLayout; coupon?: string }) =>
    request<RemixState>("PUT", `${remixBase(slug)}/${id}`, body),
  compose: (slug: string, id: string, direction?: string) => request<RemixState>("POST", `${remixBase(slug)}/${id}/compose`, { direction }),
  create: (slug: string, id: string) => request<{ klaviyo_template_id: string; editor_url: string }>("POST", `${remixBase(slug)}/${id}/create`),
  remove: (slug: string, id: string) => request<{ ok: boolean }>("DELETE", `${remixBase(slug)}/${id}`),
};
